/**
 * `/admin` — the tenant admin console, or the platform console for a
 * platform-admin.
 *
 * The two consoles are different screens under one address, so the branch is
 * here rather than inside either of them. Both render an `Outlet`: their
 * sections are child routes.
 *
 * @module router/admin/AdminRoute
 */

import React from "react";

import { Navigate } from "react-router";

import { AdminLayout } from "@/router/admin/AdminLayout";
import { AdminUsersRoute } from "@/router/admin/AdminSectionRoutes";
import { PlatformAdminLayout } from "@/router/admin/PlatformAdminRoutes";
import { useWorkspaceOutletContext } from "@/router/workspace-context";

/** True for the accounts the platform console belongs to. */
export function useIsPlatformAdmin(): boolean {
  return useWorkspaceOutletContext().profile.role === "platform-admin";
}

export function AdminRoute(): React.ReactElement {
  return useIsPlatformAdmin() ? <PlatformAdminLayout /> : <AdminLayout />;
}

/**
 * What `/admin` itself shows.
 *
 * A Platform Admin is forwarded to the section they land on rather than being
 * given a second address for it, so the tab strip above always marks the one
 * they are reading.
 */
export function AdminIndexRoute(): React.ReactElement {
  return useIsPlatformAdmin() ? <Navigate to="/admin/manage" replace /> : <AdminUsersRoute />;
}
